import React, { useState, useEffect } from "react";
import "./connect-contacts-page.scss";
import ContactHelper from "../../../../entities/contact/contact-helper";
import { useSelector } from "react-redux";
import OutsideClickHandler from "react-outside-click-handler";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import ContactApi from "../../../../api/contact-api";
import ThreadApi from "../../../../api/thread-api";
import ThreadHelper from "../../../../entities/thread/thread-helper";
import { EventEmitter } from "../../../../shared/utils/event-emitter";
import Events from "../../../../const/events";
import ThreadChatModal from "../../components/thread-chat-modal/thread-chat-modal";
import ContactSearch from "../../components/contact-search/contact-search";
import { Tooltip } from "react-tooltip";
import usePageInfiniteScroll from "../../../../shared/utils/use-page-infinite-scroll";

const ConnectContactsPage = () => {
  const [selectedContact1, setSelectedContact1] = useState(null);
  const [selectedContact2, setSelectedContact2] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [previousConnections, setPreviousConnections] = useState([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [connectionsPagination, setConnectionsPagination] = useState({
    page: 1,
    limit: 50,
    totalPages: 1,
    hasNextPage: false
  });

  // Get pagination info from Redux store
  const pagination = useSelector((state) => state?.threadStore?.pagination || {});
  
  // Infinite scroll setup for previous connections using page scroll
  const { isLoadingMore } = usePageInfiniteScroll(
    () => loadMoreConnections(),
    pagination.hasNextPage,
    isLoadingConnections
  );

  // States for manual contact entry
  const [showManualContact1, setShowManualContact1] = useState(false);
  const [showManualContact2, setShowManualContact2] = useState(false);
  const [contact1Name, setContact1Name] = useState("");
  const [contact1Mobile, setContact1Mobile] = useState("");
  const [contact2Name, setContact2Name] = useState("");
  const [contact2Mobile, setContact2Mobile] = useState("");

  // Function to validate mobile numbers
  const validateMobileNumber = (mobile) => {
    let value = mobile?.trim() || "";

    // Remove all special characters except "+"
    value = value.replace(/[^+\d]/g, "");

    if (value === "") {
      return { isValid: false, error: "Phone number is required." };
    }

    // Ensure it starts with "+"
    if (!value.startsWith("+")) {
      return {
        isValid: false,
        error:
          "Phone number must start with a country code (e.g., +1 for USA).",
      };
    }

    // Extract numeric part (excluding "+")
    const numericPart = value.replace("+", "");

    // Check minimum length
    if (numericPart.length < 10) {
      return {
        isValid: false,
        error:
          "Phone number must have at least 10 digits (excluding country code).",
      };
    }

    // Check maximum length (adjust as needed)
    if (numericPart.length > 15) {
      return {
        isValid: false,
        error: "Phone number must not exceed 15 digits.",
      };
    }

    return { isValid: true, formattedNumber: value };
  };

  // Process contact (check if exists or create new)
  const processContact = async (name, phone) => {
    try {
      // First check if contact exists
      const { data: checkResult } =
        await ContactApi.private.checkIfContactExists({ phone });

      // If contact exists, return the contact ID directly
      if (checkResult?.data?._id) {
        return checkResult.data._id;
      }

      // If no valid contact ID is found, throw an error
      throw new Error("Contact check returned an invalid response.");
    } catch (error) {
      // If the error indicates that the contact doesn't exist (404), create a new one
      if (error?.response?.status === 404) {
        try {
          const { contactId } = await ContactHelper.createNewContact({
            name,
            phone,
          });

          if (contactId) {
            return contactId;
          }

          throw new Error("Failed to create new contact.");
        } catch (createError) {
          throw new Error(`Contact creation failed: ${createError.message}`);
        }
      }

      // If it's a different error, rethrow it
      throw new Error(`Contact processing failed: ${error.message}`);
    }
  };

  // Get first name for room title
  const getFirstName = (fullName) => {
    return fullName.split(" ")[0];
  };

  // Create a room thread
  const createRoom = async (contact1Id, contact2Id, contact1Name, contact2Name) => {
    try {
      const roomTitle = `${getFirstName(contact1Name)}+${getFirstName(contact2Name)}`;

      const response = await ThreadApi.private.createThread({
        type: "room",
        title: roomTitle,
        _users: [],
        _contacts: [contact1Id, contact2Id],
      });

      return { success: true, data: response?.data };
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Unknown error occurred";

      if (errorMessage.includes("thread already exists for this")) {
        return { success: false, message: errorMessage };
      }

      return { success: false, message: errorMessage };
    }
  };

  // Handle the connect button click
  const handleConnectContacts = async () => {
    let contact1, contact2;

    // Determine which contacts to use (selected from search or manually entered)
    if (selectedContact1 && !showManualContact1) {
      contact1 = selectedContact1;
    } else if (showManualContact1) {
      // Validate mobile number for manual contact
      const validation1 = validateMobileNumber(contact1Mobile);
      if (!validation1.isValid) {
        setConnectionResult({
          success: false,
          message: `First contact: ${validation1.error}`,
        });
        return;
      }
      if (!contact1Name) {
        setConnectionResult({
          success: false,
          message: "First contact: Name is required",
        });
        return;
      }
      contact1 = {
        name: contact1Name,
        phone: contact1Mobile
      };
    } else {
      setConnectionResult({
        success: false,
        message: "Please select or add the first contact",
      });
      return;
    }

    if (selectedContact2 && !showManualContact2) {
      contact2 = selectedContact2;
    } else if (showManualContact2) {
      // Validate mobile number for manual contact
      const validation2 = validateMobileNumber(contact2Mobile);
      if (!validation2.isValid) {
        setConnectionResult({
          success: false,
          message: `Second contact: ${validation2.error}`,
        });
        return;
      }
      if (!contact2Name) {
        setConnectionResult({
          success: false,
          message: "Second contact: Name is required",
        });
        return;
      }
      contact2 = {
        name: contact2Name,
        phone: contact2Mobile
      };
    } else {
      setConnectionResult({
        success: false,
        message: "Please select or add the second contact",
      });
      return;
    }

    setIsProcessing(true);
    setConnectionResult(null);

    try {
      let contact1Id, contact2Id;
      
      if (contact1._id) {
        contact1Id = contact1._id;
      } else {
        contact1Id = await processContact(contact1.name, contact1.phone);
      }
      
      if (contact2._id) {
        contact2Id = contact2._id;
      } else {
        contact2Id = await processContact(contact2.name, contact2.phone);
      }

      const roomResult = await createRoom(
        contact1Id,
        contact2Id,
        contact1.name,
        contact2.name
      );

      setConnectionResult({
        success: roomResult.success,
        message: roomResult.success
          ? "✅ Room Created Successfully"
          : `${roomResult.message}`,
      });

      if (roomResult.success) {
        // Reset the form on success
        setSelectedContact1(null);
        setSelectedContact2(null);
        setShowManualContact1(false);
        setShowManualContact2(false);
        setContact1Name("");
        setContact1Mobile("");
        setContact2Name("");
        setContact2Mobile("");
        
        // Refresh the list of previous connections
        loadRoomThreads();
      }
    } catch (error) {
      setConnectionResult({
        success: false,
        message: `⚠️ ${error.message || "Error: Could not create room."}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Custom handler for ContactSearch's "Add new contact" button
  const handleAddNewContact1 = () => {
    setShowManualContact1(true);
    setSelectedContact1(null);
  };

  const handleAddNewContact2 = () => {
    setShowManualContact2(true);
    setSelectedContact2(null);
  };

  // When a contact is selected from search, clear manual inputs
  const handleContact1Select = (contact) => {
    setSelectedContact1(contact);
    setShowManualContact1(false);
  };

  const handleContact2Select = (contact) => {
    setSelectedContact2(contact);
    setShowManualContact2(false);
  };

  // Load previous room connections
  const loadRoomThreads = async () => {
    setIsLoadingConnections(true);
    try {
      await ThreadHelper.loadThreadsList();
      const { threadStore } = await import("../../../../redux/store").then(module => module.store.getState());
      const allThreads = threadStore?.allThreads || [];

      const roomThreads = allThreads.filter(thread => thread.type === "room");
      setPreviousConnections(roomThreads);
    } catch (error) {
      console.error("Error loading room threads:", error);
    } finally {
      setIsLoadingConnections(false);
    }
  };

  // Load more connections for infinite scroll
  const loadMoreConnections = async () => {
    try {
      console.log('loadMoreConnections called');
      await ThreadHelper.loadNextPage();
      const { threadStore } = await import("../../../../redux/store").then(module => module.store.getState());
      const allThreads = threadStore?.allThreads || [];
      
      const roomThreads = allThreads.filter(thread => thread.type === "room");
      setPreviousConnections(roomThreads);
    } catch (error) {
      console.error("Error loading more room threads:", error);
    }
  };

  // View chat button handler
  const handleViewChat = (thread) => {
    ThreadHelper.makeThreadActive(thread);
    EventEmitter.dispatch(Events.SHOW_THREAD_CHAT_MODAL);
  };

  // Reconnect button handler
  const handleReconnect = async (thread) => {
    if (!thread._contacts || thread._contacts.length < 2) {
      setConnectionResult({
        success: false,
        message: "⚠️ Cannot reconnect: Missing contact information",
      });
      return;
    }

    setIsProcessing(true);
    setConnectionResult(null);

    try {
      const contact1Id = thread._contacts[0]._id;
      const contact2Id = thread._contacts[1]._id;
      const contact1Name = thread._contacts[0].name;
      const contact2Name = thread._contacts[1].name;

      // Use the existing title or generate a new one
      const roomTitle = thread.title || `${getFirstName(contact1Name)}+${getFirstName(contact2Name)}`;

      const response = await ThreadApi.private.createThread({
        type: "room",
        title: roomTitle,
        _users: [],
        _contacts: [contact1Id, contact2Id],
      });

      setConnectionResult({
        success: true,
        message: "✅ Room Reconnected Successfully",
      });

      // Refresh the list of previous connections
      loadRoomThreads();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Unknown error occurred";

      if (errorMessage.includes("thread already exists for this")) {
        setConnectionResult({
          success: false,
          message: `⚠️ ${errorMessage}`,
        });
      } else {
        setConnectionResult({
          success: false,
          message: `⚠️ ${errorMessage}`,
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Load room threads on component mount
  useEffect(() => {
    loadRoomThreads();

    // Listen for the add contact event from ContactSearch component
    const addContactListener = EventEmitter.subscribe(
      Events.SHOW_ADD_CONTACT_MODAL,
      (data) => {
        // If we can determine which contact search triggered this, 
        // show the appropriate manual input
        // This will not be perfect, but it will try to detect which one is active
        const activeSearch = document.activeElement;
        const firstSearchInput = document.querySelector('.col-md:first-child .contact-search input');
        const secondSearchInput = document.querySelector('.col-md:last-child .contact-search input');

        if (firstSearchInput && activeSearch === firstSearchInput) {
          setShowManualContact1(true);
          setSelectedContact1(null);
          if (data && data.initialName) {
            setContact1Name(data.initialName);
          }
        } else if (secondSearchInput && activeSearch === secondSearchInput) {
          setShowManualContact2(true);
          setSelectedContact2(null);
          if (data && data.initialName) {
            setContact2Name(data.initialName);
          }
        } else {
          // Default to first contact if we can't determine which one
          setShowManualContact1(true);
          setSelectedContact1(null);
          if (data && data.initialName) {
            setContact1Name(data.initialName);
          }
        }
      }
    );

  }, []);

  // Components for app selection, etc., from old code
  const SelectApp = () => {
    const [openSelectPopup, setOpenSelectPopup] = useState(false);

    return (
      <div className="selectAppWrapper">
        <div className="selectedApp" onClick={(e) => setOpenSelectPopup(true)}>
          <div className="appIcon">
            <svg viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>Whatsapp-color</title> <desc>Created with Sketch.</desc> <defs> </defs> <g id="Icons" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#67C15E"> <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"> </path> </g> </g> </g></svg>
            <div className="downArrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 10L12 15L17 10" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            </div>
          </div>
        </div>

        <OutsideClickHandler onOutsideClick={(e) => setOpenSelectPopup(false)}>
          <div className={"selectAppPopup " + (openSelectPopup ? "show" : "")}>
            <div className="app active">
              <div className="appIcon"><svg viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>Whatsapp-color</title> <desc>Created with Sketch.</desc> <defs> </defs> <g id="Icons" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#67C15E"> <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"> </path> </g> </g> </g></svg></div>
              <div className="appLabel">WhatsApp</div>
            </div>
          </div>
        </OutsideClickHandler>
      </div>
    )
  }



  const SelectCountry = () => {
    const [openSelectPopup, setOpenSelectPopup] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState({
      flag: "assets/images/country/UK.png",
      name: "United Kingdom",
      code: "+44"
    });

    const countries = [
      {
        flag: "assets/images/country/UK.png",
        name: "United Kingdom",
        code: "+44"
      },
      {
        flag: "assets/images/country/US.png",
        name: "United States",
        code: "+1"
      },
      {
        flag: "assets/images/country/INDIA.png",
        name: "India",
        code: "+91"
      }
    ];

    const handleCountrySelect = (country) => {
      setSelectedCountry(country);
      setOpenSelectPopup(false);
    };

    return (
      <div className="selectCountryWrap">
        <div className="selectedCountry">
          <div className="selectedCountryIcon" onClick={(e) => setOpenSelectPopup(true)}>
            <img src={selectedCountry.flag} alt={selectedCountry.name} />
            <span className="countryCode">{selectedCountry.code}</span>
            <div className="downArrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 10L12 15L17 10" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            </div>
          </div>
        </div>

        <OutsideClickHandler onOutsideClick={(e) => setOpenSelectPopup(false)}>
          <div className={"selectCountryPopup " + (openSelectPopup ? "show" : "")}>
            {countries.map((country, index) => (
              <div key={index} className="countryIcon" onClick={() => handleCountrySelect(country)}>
                <div className="countryFlag"><img src={country.flag} alt={country.name} /></div>
                <div className="countryName">{country.name}</div>
                <div className="countryCode">{country.code}</div>
              </div>
            ))}
          </div>
        </OutsideClickHandler>
      </div>
    )
  }


  const SelectRole = () => {
    const [openSelectPopup, setOpenSelectPopup] = useState(false);
    const [selectedRole, setSelectedRole] = useState("Select Role");
    const [newRole, setNewRole] = useState(false);

    const roles = ["Admin", "User", "Guest"];

    const handleRoleSelect = (role) => {
      setSelectedRole(role);
      setOpenSelectPopup(false);
    };

    return (
      <div className="selectRoleWrapper">
        <div className="roleDropdownLabel">
          <div className="selectedRoleIcon" onClick={(e) => setOpenSelectPopup(true)}>
            <span className="countryCode">{selectedRole}</span>
            <div className="downArrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M7 10L12 15L17 10" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
            </div>
          </div>
        </div>

        <OutsideClickHandler onOutsideClick={(e) => setOpenSelectPopup(false)}>
          <div className={"roleOptions " + (openSelectPopup ? "show" : "")}>
            {roles.map((role, index) => (
              <div key={index} className="roleOption" onClick={() => handleRoleSelect(role)}>
                <span className="roleName">{role}</span>
              </div>
            ))}

            {
              newRole ?
              <div className="createNewRole">
                <input type="text" className="form-control" placeholder="Enter new role name..." />

                <div className="bouncy-button small green"><i className="fa fa-check"></i></div>
                <div className="bouncy-button small red" onClick={() => setNewRole(false)}><i className="fa fa-times"></i></div>
              </div> :
              <div className="roleOption createNew" onClick={() => setNewRole(true)}>
                <i className="fa fa-plus"></i>
                <span className="roleName">Create new</span>
              </div>
            }
          </div>
        </OutsideClickHandler>
      </div>
    )
  }



  const AddCreateContactForm = () => {
    const [showCreateContact, setShowCreateContact] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    return (
      <div className="addContactWrapper">
        {
          showCreateContact ?
          <>
            <div className="d-inline-flex align-items-center gap-2 btn btn-sm" onClick={() => setShowCreateContact(false)}>
              <i className="fa fa-arrow-left"></i> <span>Add from existing</span>
            </div>
            <div className="contactLabel newContact mb-3">
              <input type="text" className="form-control" placeholder="Enter new contact name..." />
              <SelectRole />
            </div>
          </>
          :
          <div className="contactLabel existingContact mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Enter contact details"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
            />

            {searchValue.trim() !== "" && (
              <div className="addNewCreateButton">
                <div className="bouncy-button white small" onClick={() => setShowCreateContact(true)}>
                  <i className="fa fa-plus"></i>
                  <span>Create new contact</span>
                </div>
              </div>
            )}
          </div>
        }


        <div className="messagingPlatformsWrapper card noOutline p-0 mb-3">
          <div className="card-sub-section">
            <div className="card-subheader mb-0">
              <h4>Delivery Platforms</h4>
            </div>

            <div className="messagingPlatforms">
              <div className="messagingPlatform active">
                <svg viewBox="0 0 48 48" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <title>Whatsapp-color</title> <desc>Created with Sketch.</desc> <defs> </defs> <g id="Icons" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="Color-" transform="translate(-700.000000, -360.000000)" fill="#67C15E"> <path d="M723.993033,360 C710.762252,360 700,370.765287 700,383.999801 C700,389.248451 701.692661,394.116025 704.570026,398.066947 L701.579605,406.983798 L710.804449,404.035539 C714.598605,406.546975 719.126434,408 724.006967,408 C737.237748,408 748,397.234315 748,384.000199 C748,370.765685 737.237748,360.000398 724.006967,360.000398 L723.993033,360.000398 L723.993033,360 Z M717.29285,372.190836 C716.827488,371.07628 716.474784,371.034071 715.769774,371.005401 C715.529728,370.991464 715.262214,370.977527 714.96564,370.977527 C714.04845,370.977527 713.089462,371.245514 712.511043,371.838033 C711.806033,372.557577 710.056843,374.23638 710.056843,377.679202 C710.056843,381.122023 712.567571,384.451756 712.905944,384.917648 C713.258648,385.382743 717.800808,392.55031 724.853297,395.471492 C730.368379,397.757149 732.00491,397.545307 733.260074,397.27732 C735.093658,396.882308 737.393002,395.527239 737.971421,393.891043 C738.54984,392.25405 738.54984,390.857171 738.380255,390.560912 C738.211068,390.264652 737.745308,390.095816 737.040298,389.742615 C736.335288,389.389811 732.90737,387.696673 732.25849,387.470894 C731.623543,387.231179 731.017259,387.315995 730.537963,387.99333 C729.860819,388.938653 729.198006,389.89831 728.661785,390.476494 C728.238619,390.928051 727.547144,390.984595 726.969123,390.744481 C726.193254,390.420348 724.021298,389.657798 721.340985,387.273388 C719.267356,385.42535 717.856938,383.125756 717.448104,382.434484 C717.038871,381.729275 717.405907,381.319529 717.729948,380.938852 C718.082653,380.501232 718.421026,380.191036 718.77373,379.781688 C719.126434,379.372738 719.323884,379.160897 719.549599,378.681068 C719.789645,378.215575 719.62006,377.735746 719.450874,377.382942 C719.281687,377.030139 717.871269,373.587317 717.29285,372.190836 Z" id="Whatsapp"> </path> </g> </g> </g></svg>
                <span>WhatsApp</span>
              </div>

              {/* <div className="messagingPlatform">
                <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M2 11.9556C2 8.47078 2 6.7284 2.67818 5.39739C3.27473 4.22661 4.22661 3.27473 5.39739 2.67818C6.7284 2 8.47078 2 11.9556 2H20.0444C23.5292 2 25.2716 2 26.6026 2.67818C27.7734 3.27473 28.7253 4.22661 29.3218 5.39739C30 6.7284 30 8.47078 30 11.9556V20.0444C30 23.5292 30 25.2716 29.3218 26.6026C28.7253 27.7734 27.7734 28.7253 26.6026 29.3218C25.2716 30 23.5292 30 20.0444 30H11.9556C8.47078 30 6.7284 30 5.39739 29.3218C4.22661 28.7253 3.27473 27.7734 2.67818 26.6026C2 25.2716 2 23.5292 2 20.0444V11.9556Z" fill="white"></path> <path d="M22.0515 8.52295L16.0644 13.1954L9.94043 8.52295V8.52421L9.94783 8.53053V15.0732L15.9954 19.8466L22.0515 15.2575V8.52295Z" fill="#EA4335"></path> <path d="M23.6231 7.38639L22.0508 8.52292V15.2575L26.9983 11.459V9.17074C26.9983 9.17074 26.3978 5.90258 23.6231 7.38639Z" fill="#FBBC05"></path> <path d="M22.0508 15.2575V23.9924H25.8428C25.8428 23.9924 26.9219 23.8813 26.9995 22.6513V11.459L22.0508 15.2575Z" fill="#34A853"></path> <path d="M9.94811 24.0001V15.0732L9.94043 15.0669L9.94811 24.0001Z" fill="#C5221F"></path> <path d="M9.94014 8.52404L8.37646 7.39382C5.60179 5.91001 5 9.17692 5 9.17692V11.4651L9.94014 15.0667V8.52404Z" fill="#C5221F"></path> <path d="M9.94043 8.52441V15.0671L9.94811 15.0734V8.53073L9.94043 8.52441Z" fill="#C5221F"></path> <path d="M5 11.4668V22.6591C5.07646 23.8904 6.15673 24.0003 6.15673 24.0003H9.94877L9.94014 15.0671L5 11.4668Z" fill="#4285F4"></path> </g></svg>
                <span>Email</span>
              </div> */}
            </div>
          </div>

        </div>

        <div className="phoneNumbers">
          <div className="phoneNumber">
            <label>WhatsApp: </label>
            <div className="phoneNumberWrapper">
              <SelectCountry />
              <input type="text" className="form-control" placeholder="Enter number" />
            </div>
          </div>


          {/* <div className="phoneNumber">
            <label>Email:</label>
            <div className="phoneNumberWrapper">
              <input type="email" className="form-control" placeholder="Enter email address" />
            </div>
          </div> */}
        </div>
      </div>
    )
  }

  return (
    <div className="connect-contacts-page">
      <h1 class="mb-3 mt-0">Connections</h1>
      <div className="card mt-0">
        <div class="card-header p-0 mt-0 mb-4">
          <h3 class="mb-2">Connect two contacts</h3>
          <p>Search or add new contacts to connect</p>
        </div>

        <div className="px-3">
          <div className="row">
            <div className="col-md">
              <div className="contactInputBox">
                <div className="contactOuterLabel">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                  <span>Contact One</span>
                </div>

                <div className="inputBox">
                  {showManualContact1 ? (
                    <div>
                      <input 
                        type="text" 
                        className="form-control mb-2" 
                        placeholder="Contact name" 
                        value={contact1Name}
                        onChange={(e) => setContact1Name(e.target.value)}
                      />
                      <div className="phoneNumberBox">
                        <SelectApp />
                        <div className="phoneNumberWrapper">
                          <div className="phone-input-container w-100">
                            <PhoneInputWithCountrySelect
                              placeholder="Enter phone number"
                              defaultCountry="GB"
                              value={contact1Mobile}
                              onChange={setContact1Mobile}
                              international={true}
                              withCountryCallingCode={true}
                              countryCallingCodeEditable={false}
                              countrySelectProps={{ unicodeFlags: true }}
                            />
                          </div>
                        </div>
                      </div>
                      <button 
                        className="btn btn-link btn-sm p-0 mt-2"
                        onClick={() => {
                          setShowManualContact1(false);
                          setContact1Name("");
                          setContact1Mobile("");
                        }}
                      >
                        Back to search
                      </button>
                    </div>
                  ) : (
                    <ContactSearch
                      placeholder="Search first contact..."
                      onContactSelect={handleContact1Select}
                      onAddNewContact={(searchText) => {
                        const isPhoneNumber = /^[+\d\s-]+$/.test(searchText);
                        if (isPhoneNumber) {
                          setContact1Name("");
                          setContact1Mobile(searchText);
                        } else {
                          setContact1Name(searchText || "");
                          setContact1Mobile("");
                        }
                        setShowManualContact1(true);
                      }}
                    />
                  )}

                  {/* <AddCreateContactForm /> */}
                </div>
              </div>
            </div>

            <div className="col-md-1 text-center my-4 my-md-0">
              <svg width="64" height="41" viewBox="0 0 64 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.9 6.9V1.7C17.9 1.3 17.6 1 17.2 1L1.7 11C1.3 11.3 1 12.1 1 12.4C1 12.4 1.3 13.6 1.7 13.9L17.2 24C17.6 24 17.9 23.7 17.9 23.3V18" stroke="black"/>
                <path d="M17.095 7H34V18H17" stroke="black"/>
                <path d="M46 22.8V17.6C46 17.2 46.3 16.9 46.7 16.9L62.2 26.9C62.6 27.2 62.9 28 62.9 28.3C62.9 28.3 62.6 29.5 62.2 29.8L46.7 40C46.3 40 46 39.7 46 39.3V34" stroke="black"/>
                <path d="M46.9056 23H30V34H47" stroke="black"/>
              </svg>

              <i className="fa-solid fa-arrow-right-arrow-left"></i>
            </div>

            <div className="col-md">
              <div className="contactInputBox">
                <div className="contactOuterLabel">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5 21C5 17.134 8.13401 14 12 14C15.866 14 19 17.134 19 21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                  <span>Contact Two</span>
                </div>
                <div className="inputBox">
                  {showManualContact2 ? (
                    <div>
                      <input 
                        type="text" 
                        className="form-control mb-2" 
                        placeholder="Contact name" 
                        value={contact2Name}
                        onChange={(e) => setContact2Name(e.target.value)}
                      />
                      <div className="phoneNumberBox">
                        <SelectApp />
                        <div className="phoneNumberWrapper">
                          <div className="phone-input-container w-100">
                            <PhoneInputWithCountrySelect
                              placeholder="Enter phone number"
                              defaultCountry="GB"
                              value={contact2Mobile}
                              onChange={setContact2Mobile}
                              international={true}
                              withCountryCallingCode={true}
                              countryCallingCodeEditable={false}
                              countrySelectProps={{ unicodeFlags: true }}
                              // className="form-control"
                            />
                          </div>
                        </div>
                      </div>
                      <button 
                        className="btn btn-link btn-sm p-0 mt-2"
                        onClick={() => {
                          setShowManualContact2(false);
                          setContact2Name("");
                          setContact2Mobile("");
                        }}
                      >
                        Back to search
                      </button>
                    </div>
                  ) : (
                    <ContactSearch
                      placeholder="Search second contact..."
                      onContactSelect={handleContact2Select}
                      onAddNewContact={(searchText) => {
                        const isPhoneNumber = /^[+\d\s-]+$/.test(searchText);
                        if (isPhoneNumber) {
                          setContact2Name("");
                          setContact2Mobile(searchText);
                        } else {
                          setContact2Name(searchText || "");
                          setContact2Mobile("");
                        }
                        setShowManualContact2(true);
                      }}
                    />
                  )}

                  {/* <AddCreateContactForm /> */}
                </div>
              </div>
            </div>
          </div>



          <div className="buttons text-end">
            <div
              className={`bouncy-button ${isProcessing ? 'disabled' : ''}`}
              onClick={!isProcessing ? handleConnectContacts : undefined}
            >
              <span>{isProcessing ? "Processing..." : "Connect"} </span>
              {/* {!isProcessing && (
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
              )} */}
            </div>
          </div>

          {connectionResult && (
            <div className="mt-3">
              <div className={`alert ${connectionResult.success ? 'alert-success' : 'alert-danger'}`}>
                {connectionResult.message}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card noOutline p-0 previousConnectionsWrapper mt-5">
        <div className="card-header p-0">
          <h3 className="mb-3">Previous connections</h3>
        </div>
        {isLoadingConnections ? (
          <div className="text-center my-5">
            <img src="assets/images/ring-loader.svg" alt="loader" className="loader" />
          </div>
        ) : previousConnections.length > 0 ? (
          <div className="tableWrapper" id="all_chat">
            <table className="table">
              <thead className="thead-light">
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">First Contact</th>
                  <th scope="col">Second Contact</th>
                  <th scope="col">Status</th>
                  <th scope="col">Created on</th>
                  <th scope="col">View / Reconnect</th>
                </tr>
              </thead>
              <tbody>
                {previousConnections.map((thread, index) => (
                  <tr key={thread._id}>
                    <th scope="row">{index + 1}</th>
                    <td>
                      {thread._contacts && thread._contacts[0] ? thread._contacts[0].name : "Unknown"} <br />
                      <small>{thread._contacts && thread._contacts[0] ? thread._contacts[0].phone : ""}</small>
                    </td>
                    <td>
                      {thread._contacts && thread._contacts[1] ? thread._contacts[1].name : "Unknown"} <br />
                      <small>{thread._contacts && thread._contacts[1] ? thread._contacts[1].phone : ""}</small>
                    </td>
                    <td>
                      <span className={`badge rounded-pill ${thread.lastMessagePreview && thread.lastMessagePreview.indexOf("left the room") !== -1 ? 'badge-soft-secondary' : 'badge-soft-success'}`}>
                        {thread.lastMessagePreview && thread.lastMessagePreview.indexOf("left the room") !== -1 ? 'Ended' : 'Ongoing'}
                      </span>
                    </td>
                    <td>{new Date(thread.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="bouncy-button small white me-1"
                        onClick={() => handleViewChat(thread)}
                      >
                        <i className="fa fa-eye"></i>
                        {/* <span>View chat</span> */}
                      </button>
                      <button
                        className="bouncy-button small white"
                        onClick={() => handleReconnect(thread)}
                        disabled={isProcessing}
                        data-tooltip="tooltip-reconnect"
                      >
                        <i className="mdi mdi-refresh"></i>
                        {/* <span>Reconnect</span> */}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Infinite scroll loading indicator */}
            {isLoadingMore && (
              <div className="text-center my-3">
                <img src="assets/images/ring-loader.svg" alt="loading more" className="loader" />
              </div>
            )}
          </div>
        ) : (
          <div className="alert alert-info text-center">
            No previous connections found. Connect some contacts to get started!
          </div>
        )}
      </div>

      <ThreadChatModal />
    </div>
  );
};

export default ConnectContactsPage;
